import React from 'react';
import { Notification } from '../../types';
import { useData } from '../../contexts/DataContext';
import { useTranslation } from 'react-i18next';

interface NotificationCardProps {
  notification: Notification;
  onNavigate?: (url: string) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification, onNavigate }) => {
  const { markNotificationAsRead, deleteNotification } = useData();
  const { t } = useTranslation();

  const handleMarkAsRead = async () => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }
  };

  const handleDelete = async () => {
    await deleteNotification(notification.id);
  };

  const handleClick = () => {
    if (notification.action_url && onNavigate) {
      onNavigate(notification.action_url);
    }
    handleMarkAsRead();
  };

  const getNotificationIcon = () => {
    switch (notification.type) {
      case 'new_release_pending':
        return '🎵';
      case 'release_approved':
        return '✅';
      case 'release_rejected':
        return '❌';
      case 'payment_sent':
        return '💰';
      case 'release_published':
        return '🎉';
      case 'new_artist_registered':
        return '👤';
      case 'system_update':
        return '📢';
      default:
        return '📋';
    }
  };

  const getNotificationColor = () => {
    switch (notification.type) {
      case 'new_release_pending':
        return 'border-blue-500';
      case 'release_approved':
      case 'release_published':
        return 'border-green-500';
      case 'release_rejected':
        return 'border-red-500';
      case 'payment_sent':
        return 'border-yellow-500';
      case 'new_artist_registered':
        return 'border-purple-500';
      case 'system_update':
        return 'border-gray-500';
      default:
        return 'border-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes} ${t('common.minutes')}`;
    } else if (hours < 24) {
      return `${hours} ${t('common.hours')}`;
    } else if (days < 7) {
      return `${days} ${t('common.days')}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div 
      className={`bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg shadow-md p-4 border-l-4 ${getNotificationColor()} 
        ${!notification.read ? 'ring-2 ring-purple-300/50' : ''} 
        ${notification.action_url ? 'cursor-pointer hover:bg-white/15 hover:shadow-lg' : ''} 
        transition-all duration-200`}
      onClick={notification.action_url ? handleClick : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{getNotificationIcon()}</span>
            <h3 className={`font-medium ${!notification.read ? 'text-white' : 'text-white/70'}`}>
              {notification.title}
            </h3>
            {!notification.read && (
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            )}
          </div>
          <p className={`text-sm ${!notification.read ? 'text-white/90' : 'text-white/60'}`}>
            {notification.message}
          </p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-white/50">
              {formatDate(notification.created_at)}
            </span>
            {notification.action_url && (
              <span className="text-xs text-orange-400 hover:text-orange-300">
                {t('notifications.clickToView')}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 ml-4">
          {!notification.read && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsRead();
              }}
              className="text-green-400 hover:text-green-300 text-sm"
              title={t('notifications.markAsRead')}
            >
              ✓
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            className="text-red-400 hover:text-red-300 text-sm"
            title={t('notifications.delete')}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard; 